import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  doc,
  getDoc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { ProductionLog } from '../types';
import { moldHistoryService } from './mold-history.service';
import { HistoryFormData } from '../types/history.types';

export class ProductionLogService {
  private readonly collectionName = 'production_logs';

  // 생산이력 등록
  async create(data: Omit<ProductionLog, 'id' | 'createdAt'>): Promise<string> {
    try {
      const now = Timestamp.now();
      
      const logData: Omit<ProductionLog, 'id'> = {
        ...data,
        createdAt: now,
        updatedAt: now
      };

      const docRef = await addDoc(collection(db, this.collectionName), logData);

      // 이력 자동 생성
      try {
        // Timestamp를 Date로 변환
        const logDate = data.date && typeof data.date.toDate === 'function' 
          ? data.date.toDate() 
          : new Date();

        const historyData: HistoryFormData = {
          moldId: data.moldId,
          type: 'production',
          title: '일일 생산',
          description: data.notes || `생산 기록 (${data.shots.toLocaleString()} shots)`,
          date: logDate,
          shots: data.shots,
          productionQty: data.shots,
          selectedCavities: (data as any).selectedCavities  // 캐비티 정보 전달
        };
        await moldHistoryService.create(historyData);
      } catch (error) {
        console.error('생산 이력 생성 실패:', error);
        // 이력 생성 실패해도 생산 로그는 성공으로 간주
      }

      return docRef.id;
    } catch (error) {
      console.error('생산이력 등록 실패:', error);
      throw new Error('생산이력 등록에 실패했습니다.');
    }
  }

  // 금형별 생산이력 조회
  async getByMoldId(moldId: string): Promise<ProductionLog[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('moldId', '==', moldId),
        orderBy('date', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ProductionLog));
    } catch (error) {
      console.error('금형별 생산이력 조회 실패:', error);
      throw new Error('생산이력 조회에 실패했습니다.');
    }
  }

  // 기간별 생산이력 조회
  async getByDateRange(moldId: string, startDate: Date, endDate: Date): Promise<ProductionLog[]> {
    try {
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);

      const q = query(
        collection(db, this.collectionName),
        where('moldId', '==', moldId),
        where('date', '>=', startTimestamp),
        where('date', '<=', endTimestamp),
        orderBy('date', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ProductionLog));
    } catch (error) {
      console.error('기간별 생산이력 조회 실패:', error);
      throw new Error('생산이력 조회에 실패했습니다.');
    }
  }

  // 생산이력 합계 계산
  calculateTotal(logs: ProductionLog[]): number {
    return logs.reduce((sum, log) => sum + log.shots, 0);
  }

  // 단일 생산이력 조회
  async getById(id: string): Promise<ProductionLog | null> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as ProductionLog;
      }
      return null;
    } catch (error) {
      console.error('생산이력 조회 실패:', error);
      throw new Error('생산이력 조회에 실패했습니다.');
    }
  }

  // 생산이력 수정
  async update(id: string, data: Partial<ProductionLog>): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('생산이력 수정 실패:', error);
      throw new Error('생산이력 수정에 실패했습니다.');
    }
  }

  // 생산이력 삭제
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('생산이력 삭제 실패:', error);
      throw new Error('생산이력 삭제에 실패했습니다.');
    }
  }

  // 최근 생산이력 조회 (N개)
  async getRecent(moldId: string, limit: number = 10): Promise<ProductionLog[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('moldId', '==', moldId),
        orderBy('date', 'desc')
      );

      const snapshot = await getDocs(q);
      const logs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ProductionLog));

      return logs.slice(0, limit);
    } catch (error) {
      console.error('최근 생산이력 조회 실패:', error);
      throw new Error('생산이력 조회에 실패했습니다.');
    }
  }
}

// 싱글톤 인스턴스
export const productionLogService = new ProductionLogService();
